import type { TxnType } from '../../types';

/** Base URL of the myBooksAi help/docs site (Docusaurus, served at /help/). */
export const HELP_DOCS_BASE_URL = 'https://mybooksai.app/help/docs';
export const HELP_DOCS_HOME_URL = 'https://mybooksai.app/help/';

/**
 * Maps a URL path prefix to a help-doc slug. Matched by longest-prefix-first,
 * so a more specific route (e.g. /items/view-price-lists) wins over a more
 * general one (e.g. /items) without needing manual ordering below.
 */
const ROUTE_TO_DOC_SLUG: Record<string, string> = {
  '/dashboard': 'Getting-Started',
  '/dashboard-v1': 'Getting-Started',
  '/dashboard-v2': 'Getting-Started',

  '/contacts': 'Managing-Contacts',

  '/items/create-price-list': 'Price-Lists',
  '/items/edit-price-list': 'Price-Lists',
  '/items/view-price-lists': 'Price-Lists',
  '/items': 'Items-and-Services',

  '/transactions/create-invoice': 'Sales-and-Invoicing',
  '/transactions/view-invoices': 'Sales-and-Invoicing',
  '/transactions/create-estimate': 'Estimates-and-Quotes',
  '/transactions/view-estimates': 'Estimates-and-Quotes',
  '/transactions/create-proforma-invoice': 'Sales-and-Invoicing',
  '/transactions/view-proforma-invoices': 'Sales-and-Invoicing',
  '/transactions/create-sales-return': 'Sales-Returns',
  '/transactions/view-sales-returns': 'Sales-Returns',
  '/transactions/allocate-return-txn': 'Sales-Returns',
  '/transactions/create-sales-order': 'Sales-Orders',
  '/transactions/view-sales-orders': 'Sales-Orders',
  '/transactions/create-delivery': 'Delivery-Notes',
  '/transactions/view-deliveries': 'Delivery-Notes',
  '/transactions/create-purchase-bill': 'Purchases-and-Expenses',
  '/transactions/view-purchase-bills': 'Purchases-and-Expenses',
  '/transactions/create-purchase-order': 'Purchase-Orders',
  '/transactions/view-purchase-orders': 'Purchase-Orders',
  '/transactions/create-purchase-return': 'Purchase-Returns',
  '/transactions/view-purchase-returns': 'Purchase-Returns',
  '/transactions/view-stock-adjustments': 'Stock-Adjustments',
  '/transactions/create-stock-adjustment': 'Stock-Adjustments',
  '/transactions/edit-stock-adjustment': 'Stock-Adjustments',
  '/transactions/create-expense': 'Purchases-and-Expenses',
  '/transactions/view-expenses': 'Purchases-and-Expenses',
  '/transactions/edit-expense': 'Purchases-and-Expenses',
  '/transactions/create-customer-payment': 'Customer-Payments',
  '/transactions/create-vendor-payment': 'Vendor-Payments',
  '/transactions/view-journals': 'Journal-Entries',
  '/transactions/create-journal': 'Journal-Entries',
  '/transactions/edit-journal': 'Journal-Entries',
  '/transactions/view-business-transactions': 'Business-Transactions',
  '/transactions/create-business-transaction': 'Business-Transactions',
  '/transactions/edit-business-transaction': 'Business-Transactions',
  '/transactions/tax-summary': 'Tax-Summary-and-Reports',
  '/transactions/view-ledger': 'Journal-Entries',

  '/banking': 'Banking-and-Reconciliation',
  '/documents': 'Documents',
  '/manufacturing': 'Manufacturing-Work-Orders-and-BOMs',
  '/projects': 'Projects',
  '/budgets': 'Budgets',

  '/settings/business-profile': 'Getting-Started',
  '/settings/opening-balance': 'Opening-Balances',
  '/settings/chart-of-accounts': 'Application-Settings',
  '/settings/uom': 'Items-and-Services',
  '/settings/currency': 'Multi-Currency',
  '/settings/inventory': 'Items-and-Services',
  '/settings/barcode-configuration': 'Items-and-Services',
  '/settings/proforma-invoice': 'Sales-and-Invoicing',
  '/settings/export-fields': 'Data-Import-Export',
  '/settings/delete-Account': 'Account-Deletion-and-Privacy',
  '/settings/delete-branch': 'Multi-Branch',
  '/settings/overdue-reminder': 'Payment-Reminders',
  '/settings/api-keys': 'API-Keys',
  '/settings/taxes': 'Application-Settings',
  '/settings/subscription': 'Subscription-Plans',
  '/settings/user-settings': 'Users-and-Roles',
  '/settings/auto-number': 'Auto-Numbering',
  '/settings/auto-numbering': 'Auto-Numbering',
  '/settings/users': 'Users-and-Roles',
  '/settings/roles': 'Users-and-Roles',
  '/settings/user-roles': 'Users-and-Roles',
  '/settings/adjustment-reasons': 'Stock-Adjustments',
  '/settings/auto-matching-rules': 'Banking-and-Reconciliation',
  '/settings/recurring-transactions': 'Recurring-Transactions',
  '/settings/payment-modes': 'Customer-Payments',
  '/settings/data-upload': 'Data-Import-Export',
  '/settings/sales-persons': 'Managing-Contacts',
  '/settings/contact-groups': 'Managing-Contacts',
  '/settings/item-groups': 'Items-and-Services',
  '/settings/tags': 'Tags',
  '/settings/payment-terms': 'Sales-and-Invoicing',
  '/settings/item-categories': 'Items-and-Services',
  '/settings/contact-categories': 'Managing-Contacts',
  '/settings/print-templates': 'Print-Templates',
  '/settings/template-config': 'Print-Templates',
  '/settings/recycle-bin': 'Recycle-Bin',
  '/settings/late-fee': 'Late-Fees',
  '/settings/customer-portal': 'Customer-Portal',
  '/settings/backup': 'Backup-and-Restore',
  '/settings/einvoice': 'E-Invoicing',
  '/settings/fy-lock': 'Application-Settings',
  '/settings/business-summary': 'AI-Features',
  '/settings/ai-features': 'AI-Features',
  '/settings/payment-gateway': 'Payment-Gateways',
  '/settings/projects-module': 'Projects',
  '/settings/budgets-module': 'Budgets',
  '/settings': 'Application-Settings',

  '/reports/gstr-1': 'GST-Reports',
  '/reports/gstr-2': 'GST-Reports',
  '/reports/gstr-3b': 'GST-Reports',
  '/reports/tax-reports': 'Tax-Summary-and-Reports',
  '/reports/tax-summary-report': 'Tax-Summary-and-Reports',
  '/reports/audit-report': 'Audit-Report',
  '/reports/budget-vs-actual': 'Budgets',
  '/reports': 'Understanding-Reports',
};

/** Dynamic routes of the form /transactions/(edit-txn|view-txn|send-txn)/:txnType/... */
const TXN_TYPE_TO_DOC_SLUG: Partial<Record<TxnType, string>> = {
  Invoice: 'Sales-and-Invoicing',
  Estimate: 'Estimates-and-Quotes',
  ProformaInvoice: 'Sales-and-Invoicing',
  SalesOrder: 'Sales-Orders',
  Delivery: 'Delivery-Notes',
  SalesReturn: 'Sales-Returns',
  Bills: 'Purchases-and-Expenses',
  PurchaseBills: 'Purchases-and-Expenses',
  Expense: 'Purchases-and-Expenses',
  PurchaseOrder: 'Purchase-Orders',
  PurchaseReturn: 'Purchase-Returns',
  StockAdjustment: 'Stock-Adjustments',
  OpeningReceivable: 'Opening-Balances',
  OpeningPayables: 'Opening-Balances',
};

/**
 * Resolves the current app route to a help-doc slug, or undefined if there's
 * no specific match (caller should fall back to the docs homepage).
 */
export const getHelpSlugForPath = (pathname: string): string | undefined => {
  const dynamicTxnMatch = pathname.match(/^\/transactions\/(edit-txn|view-txn|send-txn)\/([^/]+)/);
  if (dynamicTxnMatch) {
    const txnType = dynamicTxnMatch[2] as TxnType;
    if (TXN_TYPE_TO_DOC_SLUG[txnType]) return TXN_TYPE_TO_DOC_SLUG[txnType];
  }

  const paymentMatch = pathname.match(/^\/transactions\/(view-payments|edit-payment|send-payment|view-payment)\/([^/]+)/);
  if (paymentMatch) {
    return paymentMatch[2].includes('Vendor') ? 'Vendor-Payments' : 'Customer-Payments';
  }

  const prefixes = Object.keys(ROUTE_TO_DOC_SLUG).sort((a, b) => b.length - a.length);
  const matchedPrefix = prefixes.find((prefix) => pathname.startsWith(prefix));
  return matchedPrefix ? ROUTE_TO_DOC_SLUG[matchedPrefix] : undefined;
};

/** Full help-doc URL for the current route, falling back to the docs homepage. */
export const getHelpUrlForPath = (pathname: string): string => {
  const slug = getHelpSlugForPath(pathname);
  return slug ? `${HELP_DOCS_BASE_URL}/${slug}` : HELP_DOCS_HOME_URL;
};
